import * as Sentry from '@sentry/react';
import { addBreadcrumb, reportError } from '@/utils/sentry';
import { handleHttpError, isErrorPage } from '@/utils/errorHandler';
import { toast } from 'sonner';
import i18n from '@/i18n';
import {
  classifyRequest,
  isRedirectEligibleRequest,
  shouldReportRequestToSentry,
  shouldShowApiErrorToast,
} from '@/utils/requestClassification';

const SUPPRESS_GLOBAL_ERROR_REPORTING_KEY = '__skipGlobalErrorReporting';

type ReportableError = Error & {
  [SUPPRESS_GLOBAL_ERROR_REPORTING_KEY]?: boolean;
};

const shouldSkipGlobalErrorReporting = (error: unknown): boolean => {
  return Boolean(
    error &&
    typeof error === 'object' &&
    SUPPRESS_GLOBAL_ERROR_REPORTING_KEY in error &&
    (error as ReportableError)[SUPPRESS_GLOBAL_ERROR_REPORTING_KEY]
  );
};

const markErrorAsNonReportable = <TError extends Error>(error: TError): TError => {
  (error as ReportableError)[SUPPRESS_GLOBAL_ERROR_REPORTING_KEY] = true;
  return error;
};

/**
 * Show a toast notification for server errors
 */
const showServerErrorToast = (statusCode: number) => {
  const title = i18n.t('toast.serverError.title');
  const description = i18n.t('toast.serverError.description', {
    statusCode,
  });

  toast.error(title, {
    description,
    duration: 5000,
  });
};

/**
 * Show a toast notification for network errors
 */
const showNetworkErrorToast = () => {
  const title = i18n.t('toast.networkError.title');
  const description = i18n.t('toast.networkError.description');

  toast.error(title, {
    description,
    duration: 5000,
  });
};

export const setupGlobalErrorHandlers = () => {
  const isDevelopment = import.meta.env.MODE !== 'production';
  const shouldSkipGlobalHandlers = isDevelopment;

  if (shouldSkipGlobalHandlers) {
    // In development, still set up minimal error handling for toasts
    // but skip Sentry-related handlers
    setupMinimalErrorHandlers();
    return;
  }
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    if (shouldSkipGlobalErrorReporting(error)) {
      event.preventDefault();
      return;
    }

    addBreadcrumb(
      `Unhandled promise rejection: ${error.message}`,
      'error',
      'error'
    );

    reportError(error, {
      type: 'unhandledRejection',
      source: 'globalHandler',
      url: window.location.href,
      stack: error.stack,
    });

    // Prevent the default browser behavior (logging to console)
    event.preventDefault();
  });

  // Handle global JavaScript errors that escape React error boundaries
  window.addEventListener('error', (event) => {
    if (event.filename) {
      const requestDetails = classifyRequest(event.filename);

      if (!shouldReportRequestToSentry(requestDetails.classification)) {
        return;
      }
    }

    const error =
      event.error instanceof Error
        ? event.error
        : new Error(event.message || 'Unknown error');

    if (shouldSkipGlobalErrorReporting(error)) {
      return;
    }

    addBreadcrumb(`Global error: ${error.message}`, 'error', 'error');

    reportError(error, {
      type: 'globalError',
      source: 'globalHandler',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      url: window.location.href,
    });
  });

  // Handle network errors and resource loading failures
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target as HTMLElement | null;

      // Check if it's a resource loading error (img, script, link, etc.)
      if (target && target !== (window as any) && target.tagName) {
        const resourceType = target.tagName.toLowerCase();
        const resourceUrl = (target as any).src || (target as any).href;

        if (resourceUrl) {
          const requestDetails = classifyRequest(resourceUrl);

          addBreadcrumb(
            `Resource failed to load: ${resourceType} - ${resourceUrl}`,
            'resource',
            'warning'
          );

          if (
            !isDevelopment &&
            shouldReportRequestToSentry(requestDetails.classification)
          ) {
            Sentry.captureMessage(`Resource loading failed: ${resourceType}`, {
              level: 'warning',
              tags: {
                requestClassification: requestDetails.classification,
                resourceType,
                resourceUrl,
              },
              extra: {
                element: target.outerHTML?.slice(0, 200) + '...',
                url: window.location.href,
              },
            });
          }
        }
      }
    },
    true
  ); // Use capture phase for resource errors

  // Track navigation errors
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args) {
    addBreadcrumb(`Navigation: pushState to ${args[2]}`, 'navigation', 'info');
    return originalPushState.apply(this, args);
  };

  history.replaceState = function (...args) {
    addBreadcrumb(
      `Navigation: replaceState to ${args[2]}`,
      'navigation',
      'info'
    );
    return originalReplaceState.apply(this, args);
  };

  // Track fetch errors globally
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const requestDetails = classifyRequest(args[0], args[1]);
    const { classification, method, origin, url } = requestDetails;
    const shouldReportToSentry = shouldReportRequestToSentry(classification);

    addBreadcrumb(`Fetch: ${method} ${url}`, 'http', 'info');
    addBreadcrumb(
      `Fetch classified as ${classification}: ${method} ${url}`,
      'http',
      'debug'
    );

    try {
      const response = await originalFetch.apply(this, args);

      if (!response.ok) {
        addBreadcrumb(
          `Fetch error: ${method} ${url} - ${response?.status} ${response?.statusText}`,
          'http',
          'warning'
        );

        const isRedirectEligible = isRedirectEligibleRequest(classification);

        if (isRedirectEligible && !isErrorPage()) {
          handleHttpError(
            response?.status,
            new Error(`HTTP ${response?.status}: ${response?.statusText}`),
            {
              classification,
              method,
              origin,
              url,
              source: 'globalFetchHandler',
            }
          );
        } else {
          const level = response.status >= 500 ? 'error' : 'warning';

          if (
            response.status >= 500 &&
            shouldShowApiErrorToast(classification)
          ) {
            showServerErrorToast(response.status);
          }

          if (!isDevelopment && shouldReportToSentry) {
            Sentry.captureMessage(`HTTP ${response.status}: ${method} ${url}`, {
              level,
              tags: {
                httpStatus: response.status,
                httpMethod: method,
                requestClassification: classification,
                url,
              },
              extra: {
                origin,
                isRedirectEligible,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries()),
              },
            });
          }
        }
      }

      return response;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));

      // Skip toast and logging for aborted requests
      if (err.name === 'AbortError') {
        throw error;
      }

      addBreadcrumb(
        `Fetch failed: ${method} ${url} - ${err.message}`,
        'http',
        'error'
      );

      if (shouldShowApiErrorToast(classification)) {
        showNetworkErrorToast();
      }

      if (shouldReportToSentry) {
        reportError(err, {
          classification,
          type: 'fetchError',
          method,
          origin,
          url,
          source: 'globalFetchHandler',
        });
      } else {
        markErrorAsNonReportable(err);

        addBreadcrumb(
          `Suppressed third-party fetch error reporting: ${classification} ${method} ${url}`,
          'http',
          'info'
        );
      }

      throw error;
    }
  };

  // Set up performance observer for long tasks
  if ('PerformanceObserver' in window) {
    try {
      const perfObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'longtask') {
            addBreadcrumb(
              `Long task detected: ${entry.duration.toFixed(2)}ms`,
              'performance',
              'warning'
            );

            // Report very long tasks (>100ms) as performance issues
            if (entry.duration > 100 && !isDevelopment) {
              Sentry.captureMessage(
                `Long task: ${entry.duration.toFixed(2)}ms`,
                {
                  level: 'warning',
                  tags: {
                    performanceIssue: 'longTask',
                  },
                  extra: {
                    duration: entry.duration,
                    startTime: entry.startTime,
                    url: window.location.href,
                  },
                }
              );
            }
          }
        }
      });

      perfObserver.observe({ entryTypes: ['longtask'] });
    } catch (error) {
      console.warn(
        'Performance observer not supported or failed to initialize'
      );
    }
  }

  // Global async operation wrapper utility
  const createGlobalAsyncWrapper = () => {
    return <T extends any[], R>(
      fn: (...args: T) => Promise<R>,
      context?: Record<string, any>
    ): ((...args: T) => Promise<R>) => {
      return async (...args: T): Promise<R> => {
        const functionName = fn.name || 'anonymous';

        try {
          addBreadcrumb(
            `Async operation started: ${functionName}`,
            'async',
            'debug'
          );

          const result = await fn(...args);

          addBreadcrumb(
            `Async operation completed: ${functionName}`,
            'async',
            'debug'
          );

          return result;
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));

          addBreadcrumb(
            `Async operation failed: ${functionName} - ${err.message}`,
            'async',
            'error'
          );

          reportError(err, {
            type: 'asyncError',
            functionName,
            source: 'globalAsyncWrapper',
            ...context,
          });

          throw error; // Re-throw to maintain normal error flow
        }
      };
    };
  };

  // Make the async wrapper globally available
  window.captureAsync = createGlobalAsyncWrapper();

  // Enhanced Promise.allSettled that reports errors
  const originalAllSettled = Promise.allSettled;
  Promise.allSettled = async <T extends readonly unknown[] | []>(
    values: T
  ): Promise<{
    -readonly [P in keyof T]: PromiseSettledResult<Awaited<T[P]>>;
  }> => {
    const results = (await originalAllSettled.call(Promise, values)) as {
      -readonly [P in keyof T]: PromiseSettledResult<Awaited<T[P]>>;
    };

    // Report any rejected promises
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        const error =
          result.reason instanceof Error
            ? result.reason
            : new Error(String(result.reason));

        if (shouldSkipGlobalErrorReporting(error)) {
          return;
        }

        addBreadcrumb(
          `Promise.allSettled rejection [${index}]: ${error.message}`,
          'promise',
          'warning'
        );

        reportError(error, {
          type: 'promiseAllSettledRejection',
          promiseIndex: index,
          source: 'globalPromiseHandler',
        });
      }
    });

    return results;
  };
};

/**
 * Minimal error handlers for development mode
 * Only tracks fetch errors for toast notifications, skips Sentry integration
 */
const setupMinimalErrorHandlers = () => {
  console.info(
    '[Dev Mode] Setting up minimal error handlers (Sentry disabled)'
  );

  // Track fetch errors globally for toast notifications
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const requestDetails = classifyRequest(args[0], args[1]);
    const { classification, method, url } = requestDetails;

    try {
      const response = await originalFetch.apply(this, args);

      if (!response.ok) {
        if (shouldShowApiErrorToast(classification)) {
          if (response.status >= 500) {
            showServerErrorToast(response.status);
          }

          console.warn(
            `[Dev Mode] API Error: ${response.status} ${response.statusText}`,
            method,
            url,
            classification
          );
        }
      }

      return response;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));

      // Skip toast and logging for aborted requests
      if (err.name === 'AbortError') {
        throw error;
      }

      if (shouldShowApiErrorToast(classification)) {
        showNetworkErrorToast();
        console.error(
          '[Dev Mode] Network Error:',
          err.message,
          method,
          url,
          classification
        );
      }

      throw error;
    }
  };
};
